// The lobby kit: the markup that the hosting window of the arcade and the
// custom lobby of the hub share (ui-map-interface.md §16.17, R3087).
//
// Both windows show the same three things in the same way:
//
//   - the code rows: CODE with the three symbols and COPY, and LINK with
//     the address, COPY and SHARE (arcade.md §16.2);
//   - one row a person: the name, the marks after it, a spacer and the
//     acts the reader may press on that person;
//   - the two-press gate of a destructive act (R837): every such act
//     carries its own arm key, so one armed row never fires another act.
//
// The kit holds no look of its own. Each host passes its press builder and
// its class words, so the arcade keeps its markup to the byte and the hub
// draws the same rows in the choice kit. The kit holds no state either:
// the host holds its `ArmGate` and its share sink.

import type { ArmGate } from './arm';
import { esc } from './esc';
import { handOn, type ShareKind } from './share';

/** How one host draws a plain press: `act` is the act word the host's own
 *  dispatcher reads, `cls` a class word the host adds for a layout. */
export type KitPress = (act: string, label: string, cls?: string) => string;

/** The words and the looks of the code rows. */
export interface CodeRowsLook {
  press: KitPress;
  /** The words of the two rows, in the reader's language. */
  words: { code: string; link: string; copy: string; share: string };
  /** The class of the caption of a row (`kick` in the arcade). */
  kick: string;
  /** The extra class of SHARE, for a host that styles it apart. */
  shareCls?: string;
  /** The classes of the two rows; the arcade's `row hw-code` and
   *  `row hw-link` when absent. */
  rows?: { code: string; link: string };
}

/** The act words of the three presses of the code rows. A host routes
 *  each of them to `handCode`. */
export const CODE_ACTS = { copyCode: 'copycode', copyLink: 'copylink', shareLink: 'sharelink' } as const;

/**
 * The two code rows: CODE with the symbols and COPY, and LINK with the
 * address, COPY and SHARE. The code and the link are text a server wrote,
 * so both are escaped here.
 */
export function codeRowsHtml(code: string, link: string, look: CodeRowsLook,
  acts: { copyCode: string; copyLink: string; shareLink: string } = CODE_ACTS): string {
  const w = look.words;
  const rows = look.rows ?? { code: 'row hw-code', link: 'row hw-link' };
  return `<div class="${rows.code}">`
    + `<span class="${look.kick}">${w.code}</span><span class="code">${esc(code)}</span>`
    + '<span class="grow"></span>'
    + look.press(acts.copyCode, w.copy)
    + '</div>'
    + `<div class="${rows.link}">`
    + `<span class="${look.kick}">${w.link}</span><span class="lnk">${esc(link)}</span>`
    + '<span class="grow"></span>'
    + look.press(acts.copyLink, w.copy)
    + look.press(acts.shareLink, w.share, look.shareCls)
    + '</div>';
}

/**
 * One person row: `cls` names the row (`hw-obs` in the arcade), `nameHtml`
 * is the name and its marks as markup the host built (the host escapes the
 * name), and `acts` the presses of the row.
 */
export function personRowHtml(cls: string, nameHtml: string, acts: string, actsCls = 'hw-acts'): string {
  return `<div class="${cls}">${nameHtml}<span class="grow"></span>`
    + `<span class="${actsCls}">${acts}</span></div>`;
}

/**
 * The press of a control that may carry an arm key. A control with no key
 * fires at once. A control with a key fires on the second press of the same
 * key inside the window of the gate, and the first press arms it (R837).
 */
export function armThrough(gate: ArmGate, key: string): boolean {
  return key === '' || gate.press(key);
}

/** The sink of COPY and SHARE: the share sheet, or the clipboard, and one
 *  line that says which. A host passes its own sink in a test. */
export type ShareSink = (text: string, kind: ShareKind, notify: (line: string) => void) => void;

/** Hands one code or one link on, through the host's sink or the page's
 *  share sheet. An empty text hands nothing on. */
export function handCode(text: string, kind: ShareKind, notify: (line: string) => void,
  sink: ShareSink = handOn): void {
  if (text === '') return;
  sink(text, kind, notify);
}
